import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { logoutAndRedirect } from '../utils/logout';
import { isSocialLogin, obSet, OB_KEYS, isEmailOtpVerified, setEmailOtpVerified, cleanEmail, safeSetItem, saveAuthToken, getAuthToken, saveSignupPasswordSession } from '../utils/onboardingStorage';
import { restoreOnboardingState } from '../utils/restoreOnboardingState';
import { LoginResponse } from '../types/auth';
import { encrypt } from 'customhooks/helper';
import { setDomainVerified, setSuspended } from 'store/slices/authSlice';
import { updateHeaderData } from 'store/slices/uiSlice';
import { useDispatch } from 'react-redux';

interface LoginCredentials {
  email: string;
  password?: string;
  social_type?: 'email' | 'google' | 'linkedin';
  full_name?: string;
  social_id?: string;
  profile_picture?: string;
  rememberMe?: boolean;
}

export const useAuthFallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigationHandledRef = useRef(false);

  const isAuthenticated = useCallback(() => {
    return !!getAuthToken();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear progress cache on hook initialization
  const clearProgressCache = useCallback(() => {
    apiService.clearProgressCache();
    localStorage.removeItem('onboarding_cached_progress');
  }, []);

  // Helper function to handle post-login navigation based on progress
  const handlePostLoginNavigation = useCallback(async (loginResponse: LoginResponse, socialTypeHint?: string) => {
    try {
      if (loginResponse.data?.email) {
        safeSetItem('email', loginResponse.data.email, 'post-login');
      }
      if (loginResponse.data?.full_name) {
        safeSetItem('full_name', loginResponse.data.full_name, 'post-login');
      }

      // Update header data from login response, falling back to cached header data if fields are missing in response
      let cachedHeader: any = {};
      try {
        const stored = localStorage.getItem('login_header_data') || localStorage.getItem('header_data');
        if (stored) cachedHeader = JSON.parse(stored);
      } catch {}

      const loginData = loginResponse.data as any;
      const loginEmail =
        loginData.user_email ||
        loginResponse.data?.email ||
        cachedHeader.email ||
        '';
      const loginCompanyName =
        loginData.company_name ||
        (loginData.companies && loginData.companies.length > 0 ? loginData.companies[0].name : '') ||
        loginData.user_other_companies?.[0]?.name ||
        cachedHeader.companyName ||
        '';
      const loginCompanyLogo =
        loginData.company_logo ||
        (loginData.companies && loginData.companies.length > 0 ? loginData.companies[0].logo_image_url : '') ||
        loginData.logo_image_url ||
        loginData.company_img ||
        cachedHeader.companyLogo ||
        '';

      dispatch(updateHeaderData({
        email: loginEmail,
        companyName: loginCompanyName,
        companyLogo: loginCompanyLogo,
        source: 'Login',
      }));

      // Prevent duplicate navigation handling
      if (navigationHandledRef.current) {
        return;
      }
      navigationHandledRef.current = true;

      const userEmail = cleanEmail(loginResponse.data.email);
      const responseSocialType = loginResponse.data.social_type;
      const isSocial = (socialTypeHint && socialTypeHint !== 'email') ||
        (responseSocialType && responseSocialType !== 'email') ||
        isSocialLogin();

      // Store social type for onboarding
      if (loginResponse.data.social_type) {
        localStorage.setItem('social_type', JSON.stringify(loginResponse.data.social_type));
      }

      // Store profile detail if available for pre-filling forms
      if (loginResponse.data.profile_detail) {
        try {
          const profileDetail = loginResponse.data.profile_detail;
          const profileDetailStr = typeof profileDetail === 'object' 
            ? JSON.stringify(profileDetail) 
            : profileDetail;
          
          localStorage.setItem('onboarding_profile_detail', profileDetailStr);
          localStorage.setItem('onboarding_BasicDetail', profileDetailStr);
        } catch (error) {
          console.error('Error storing profile detail:', error);
        }
      }

      // Always use the progress API to determine next step - don't rely on initial login response
      try {
        // Clear progress cache to ensure we get fresh data after login
        apiService.clearProgressCache();
        // Add a delay to ensure backend has processed the login and set up progress
        await new Promise(resolve => setTimeout(resolve, 300));

        const progressResponse = await apiService.getSignupProgress();

        if (progressResponse.status && progressResponse.data) {
          const { steps, is_complete, next_incomplete_step, is_suspended } = progressResponse.data;
          if (is_suspended !== undefined && is_suspended !== null) {
            dispatch(setSuspended(!!is_suspended));
          }

          // Restore state, email verification & storage for all completed steps before navigating
          await restoreOnboardingState(progressResponse.data, userEmail, dispatch);

          // If onboarding is complete on progress API, redirect to main app
          if (is_complete) {
            toast.success('Login successful! Welcome back.', { toastId: 'auth_success_toast' });
            navigate('/');
            return;
          }

          const isEmailVerified =
            progressResponse.data.email_verified === true ||
            (progressResponse.data as any).email_verified === 1 ||
            (progressResponse.data as any).email_verified === '1' ||
            isSocial ||
            isEmailOtpVerified(userEmail);

          if (isEmailVerified) {
            setEmailOtpVerified(userEmail, true);
            obSet(OB_KEYS.EMAIL_VERIFIED);
          }

          // If email is not verified, redirect to email-otp (skip for social logins or verified users)
          if (!isEmailVerified && !isSocial) {
            localStorage.setItem('tnb_otp_email', loginResponse.data.email || userEmail || '');
            navigate('/onboarding/email-otp');
            return;
          }

          // Use the next_incomplete_step from the API response
          if (next_incomplete_step) {
            const nextStep = steps.find(step => step.step_id === next_incomplete_step);
            
            if (nextStep && nextStep.route_path) {
              navigate(nextStep.route_path);
              return;
            }
          }

          // Fallback: Find the first incomplete step (status_id = 2 means not completed)
          const nextIncompleteStep = steps.find(step => step.status_id === 2);

          if (nextIncompleteStep && nextIncompleteStep.route_path) {
            navigate(nextIncompleteStep.route_path);
          } else {
            // All steps complete, redirect to main app
            toast.success('Login successful! Welcome back.', { toastId: 'auth_success_toast' });
            navigate('/');
          }
        } else {
          // Fallback if progress API fails
          console.warn('Progress API returned unsuccessful response, falling back to basic details');
          navigate('/onboarding/basic-details');
        }
      } catch (progressError: any) {
        console.error('Failed to get signup progress:', progressError);
        
        // Check if this is a 404 (user not found) - likely a new user
        if (progressError.response?.status === 404 || progressError.message?.includes('not found')) {

          navigate('/onboarding/basic-details');
        } else {
          // Show user-friendly error message but continue with fallback
          toast.warning('Unable to load your progress. Redirecting to continue onboarding.');
          // Fallback to basic details
          navigate('/onboarding/basic-details');
        }
      }
    } catch (error) {
      console.error('Post-login navigation error:', error);
      // Ultimate fallback
      navigate('/onboarding/basic-details');
    }
  }, [navigate]);



  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);
    // Reset navigation flag for new login attempt
    navigationHandledRef.current = false;

    try {
      const { rememberMe, ...apiPayload } = credentials;

      if (credentials.password) {
        saveSignupPasswordSession(credentials.password);
      }

      const response = await apiService.login(apiPayload);
      console.log("[useAuthFallback] Login response:", response);

      if (response.status) {
        // ── OTP required: password verified, need OTP to complete login ──
        if (response.data?.otp_required) {
          const resData = response.data as any;
          const loginEmail = resData.user_email || resData.email || credentials.email || '';
          const loginCompanyName = resData.company_name || '';
          const loginCompanyLogo = resData.company_logo || '';
          const authToken = resData.auth_token || '';

          dispatch(updateHeaderData({
            email: loginEmail,
            companyName: loginCompanyName,
            companyLogo: loginCompanyLogo,
            source: 'Login',
          }));

          // Cache header info temporarily for post-OTP navigation
          try {
            localStorage.setItem('login_header_data', JSON.stringify({
              email: loginEmail,
              companyName: loginCompanyName,
              companyLogo: loginCompanyLogo,
            }));
          } catch {}

          // Store email and temporary login_otp_auth_token for OTP verification page & resend OTP
          localStorage.setItem('login_otp_email', loginEmail);
          if (authToken) {
            localStorage.setItem('login_otp_auth_token', authToken);
          }
          navigate('/verify-email');
          return response;
        }

        if (response?.data?.domain_verified !== null && response?.data?.domain_verified !== undefined) {
          dispatch(setDomainVerified(!!response.data.domain_verified));
        }
        if (response?.data?.is_suspended !== null && response?.data?.is_suspended !== undefined) {
          dispatch(setSuspended(!!response.data.is_suspended));
        }

        const isRememberMe = credentials?.rememberMe ?? (localStorage.getItem('rememberMe') === 'true');
        saveAuthToken(response.data.auth_token, isRememberMe);


        // Handle post-login navigation using progress API
        await handlePostLoginNavigation(response, credentials.social_type);

        return response;
      } else {
        setError(response.message);
        toast.error(response.message);
        return response;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, handlePostLoginNavigation]);

  const verifyLoginOtp = useCallback(async (email: string, otp: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.verifyLoginOtp(email, otp);
      console.log("[useAuthFallback] verifyLoginOtp response:", response);

      if (response.status) {
        if (response?.data?.domain_verified !== null && response?.data?.domain_verified !== undefined) {
          dispatch(setDomainVerified(!!response.data.domain_verified));
        }
        if (response?.data?.is_suspended !== null && response?.data?.is_suspended !== undefined) {
          dispatch(setSuspended(!!response.data.is_suspended));
        }

        // Set auth token based on rememberMe preference
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        saveAuthToken(response.data.auth_token, isRememberMe);

        // Store email and name
        const userEmail = cleanEmail(response.data.email || email);
        if (userEmail) {
          setEmailOtpVerified(userEmail, true);
          obSet(OB_KEYS.EMAIL_VERIFIED);
        }
        localStorage.setItem('email', JSON.stringify(response.data.email || userEmail));
        localStorage.setItem('full_name', JSON.stringify(response.data.full_name));

        // Clear the login OTP email & token
        localStorage.removeItem('login_otp_email');
        localStorage.removeItem('login_otp_auth_token');

        // Handle post-login navigation
        await handlePostLoginNavigation(response, 'email');
        localStorage.removeItem('login_header_data');

        return response;
      } else {
        setError(response.message);
        toast.error(response.message);
        return response;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'OTP verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handlePostLoginNavigation, dispatch]);

  const loginWithGoogle = useCallback(async (googleToken: string, userInfo: any) => {
    const credentials: LoginCredentials = {
      full_name: userInfo.name,
      email: userInfo.email,
      social_id: googleToken,
      profile_picture: userInfo.picture,
      social_type: 'google'
    };

    // Call login first, then store social data
    const response = await login(credentials);

    // Only store social data if onboarding is NOT complete
    // This prevents the isNewUserSignup check from incorrectly identifying logged-in users as new signups
    if (response && response.status && !response.data.onboarding) {
      // Store social_type for onboarding flow
      localStorage.setItem('social_type', JSON.stringify('google'));

      // Store Google login data for prefilling forms
      if (userInfo.name && userInfo.email) {
        localStorage.setItem('loginWithGoogle', JSON.stringify({
          name: userInfo.name,
          email: userInfo.email
        }));
      }
    }

    return response;
  }, [login]);

  const loginWithLinkedIn = useCallback(async (code: string, redirectUri: string) => {
    setIsLoading(true);
    setError(null);
    // Reset navigation flag for new login attempt
    navigationHandledRef.current = false;

    try {
      // Store social_type FIRST before API call
      localStorage.setItem('social_type', JSON.stringify('linkedin'));
      
      const response = await apiService.post<LoginResponse>('/auth/linkedin/signin', {
        code,
        redirect_uri: redirectUri
      });

      if (response.status) {
        localStorage.setItem('authToken', response.data.auth_token);
        localStorage.setItem('token', response.data.auth_token);

        // Store LinkedIn login data for prefilling forms
        if (response.data.full_name && response.data.email) {
          localStorage.setItem('loginWithLinkedin', JSON.stringify({
            name: response.data.full_name,
            email: response.data.email
          }));
        }

        // Set LinkedIn as social type
        const responseWithLinkedInType = {
          ...response,
          data: {
            ...response.data,
            social_type: 'linkedin'
          }
        };

        // Handle post-login navigation using progress API
        await handlePostLoginNavigation(responseWithLinkedInType, 'linkedin');

        return response;
      } else {
        setError(response.message);
        toast.error(response.message);
        return response;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'LinkedIn login failed';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, handlePostLoginNavigation]);

  const logout = useCallback(async () => {
    try {
      await logoutAndRedirect('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect even if logout fails
      window.location.href = '/login';
    }
  }, []);

  const validateSession = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    return true;
  }, []);

  return {
    // State
    user: null,
    isAuthenticated: isAuthenticated(),
    isLoading,
    error,

    // Actions
    login,
    verifyLoginOtp,
    loginWithGoogle,
    loginWithLinkedIn,
    logout,
    validateSession,
    clearError
  };
};